import * as THREE from 'three';

/**
 * The ink omen, seen (cosmetic only): inked nightmares drip as they walk and burst into glossy black
 * splats that stay on the floor; a hero who steps in one leaves black footprints for a while. Every mark
 * is one instance of a single floor quad: its shape, birth and fade live in instance attributes, so
 * nothing is touched per frame but a clock.
 */

const VERT = /* glsl */ `
attribute vec4 aMark; // birth (s), life (s), seed, kind (0 splat, 1 drip, 2 foot)
uniform float uTime;
varying vec2 vUv;
varying float vFade;
varying float vSeed;
varying float vKind;
void main() {
  vUv = uv * 2.0 - 1.0;
  float age = uTime - aMark.x;
  float k = clamp(age / max(aMark.y, 0.001), 0.0, 1.0);
  // A splat spreads out in its first instant, then holds; everything fades at the end of its life.
  float grow = aMark.w < 0.5 ? smoothstep(0.0, 0.18, age) : 1.0;
  vFade = (1.0 - smoothstep(0.7, 1.0, k)) * step(0.0, age);
  vSeed = aMark.z;
  vKind = aMark.w;
  vec3 p = position * mix(0.35, 1.0, grow);
  gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(p, 1.0);
}`;

const FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vUv;
varying float vFade;
varying float vSeed;
varying float vKind;
float hash(float n) { return fract(sin(n) * 43758.5453); }
void main() {
  float r = length(vUv);
  float a = atan(vUv.y, vUv.x);
  float edge;
  if (vKind < 0.5) {
    // A splat: a lumpy blob with a few fingers thrown out.
    float lump = 0.62 + 0.1 * sin(a * 3.0 + vSeed * 6.28) + 0.07 * sin(a * 7.0 + vSeed * 17.0);
    float finger = pow(max(0.0, sin(a * 4.0 + vSeed * 40.0)), 10.0) * 0.22;
    edge = lump + finger;
  } else if (vKind < 1.5) {
    edge = 0.74 + 0.05 * sin(a * 3.0 + vSeed * 9.0) + 0.035 * sin(a * 5.0 + vSeed * 23.0);
  } else {
    // A footprint: a sole and a heel.
    vec2 q = vUv;
    float sole = length(vec2(q.x * 1.9, (q.y - 0.18) * 1.05));
    float heel = length(vec2(q.x * 2.2, (q.y + 0.55) * 1.6));
    r = min(sole, heel);
    edge = 0.72;
  }
  // Soft edge by screen size: tiny drops stay round instead of turning into pixel squares.
  float aa = max(fwidth(r) * 1.5, 0.04);
  float inside = 1.0 - smoothstep(edge - aa, edge + aa * 0.5, r);
  if (inside <= 0.0) discard;
  // Wet black with a violet sheen that drifts, brighter on the rim.
  float rim = smoothstep(edge - 0.3, edge - 0.04, r);
  float sheen = 0.5 + 0.5 * sin(vUv.x * 5.0 + vUv.y * 3.0 + uTime * 1.3 + vSeed * 20.0);
  vec3 col = mix(vec3(0.012, 0.008, 0.02), vec3(0.3, 0.11, 0.5), rim * 0.32 + sheen * rim * 0.18);
  float spec = pow(max(0.0, 1.0 - length(vUv - vec2(-0.25, 0.3)) * 2.2), 6.0) * (vKind < 1.5 ? 0.5 : 0.15);
  col += vec3(0.75, 0.6, 1.0) * spec;
  float alpha = inside * vFade * (vKind > 1.5 ? 0.7 : 0.92);
  gl_FragColor = vec4(col, alpha);
}`;

const SPLATS = 180;
const SMALL = 420;

/** Seconds a mark stays (it fades in the last 30 %). */
const LIFE = { splat: 75, drip: 5, foot: 9 };
/** A hero who steps in fresh ink leaves prints for this long, one every this far (world units). */
const INKY_SECONDS = 5;
const STRIDE = 0.42;

class Pool {
  readonly mesh: THREE.InstancedMesh;
  private next = 0;
  private mark: THREE.InstancedBufferAttribute;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private s = new THREE.Vector3();
  private p = new THREE.Vector3();

  constructor(size: number, uniforms: { uTime: THREE.IUniform }, order: number) {
    const geo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.mark = new THREE.InstancedBufferAttribute(new Float32Array(size * 4).fill(-1e6), 4).setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aMark', this.mark);
    const mat = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -8,
    });
    this.mesh = new THREE.InstancedMesh(geo, mat, size);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = order;
    this.m.makeScale(0, 0, 0);
    for (let i = 0; i < size; i++) this.mesh.setMatrixAt(i, this.m);
  }

  /** Puts a mark down, reusing the oldest slot when full. */
  add(x: number, y: number, z: number, sx: number, sz: number, angle: number, now: number, life: number, seed: number, kind: number): void {
    const i = this.next;
    this.next = (this.next + 1) % this.mesh.count;
    this.q.setFromAxisAngle(THREE.Object3D.DEFAULT_UP, angle);
    this.mesh.setMatrixAt(i, this.m.compose(this.p.set(x, y, z), this.q, this.s.set(sx, 1, sz)));
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mark.setXYZW(i, now, life, seed, kind);
    this.mark.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}

export class InkLayer {
  readonly group = new THREE.Group();
  private uniforms = { uTime: { value: 0 } };
  private splats = new Pool(SPLATS, this.uniforms, 1);
  private small = new Pool(SMALL, this.uniforms, 1);
  /** Fresh splats a hero can step in: world x, z, radius, when. */
  private wet: { x: number; z: number; r: number; at: number }[] = [];
  private inky = new Map<number, { until: number; x: number; z: number; left: boolean }>();
  private dripAt = new Map<number, { x: number; z: number }>();
  private seed = 0x51ed;
  private now = 0;

  constructor(
    private ground: (x: number, z: number) => number,
    private low: boolean,
  ) {
    this.group.name = 'ink';
    this.group.add(this.splats.mesh, this.small.mesh);
  }

  private rnd(): number {
    this.seed = (Math.imul(this.seed ^ (this.seed >>> 15), 2246822519) + 0x9e3779b9) >>> 0;
    return this.seed / 4294967296;
  }

  /** An inked nightmare fell here (world units): a splat and a few droplets around it. */
  splat(x: number, z: number, big: boolean): void {
    const size = (big ? 2.2 : 1.1) * (0.85 + this.rnd() * 0.35);
    this.splats.add(x, this.ground(x, z) + 0.03, z, size, size, this.rnd() * Math.PI * 2, this.now, LIFE.splat, this.rnd(), 0);
    this.wet.push({ x, z, r: size * 0.45, at: this.now });
    if (this.wet.length > 60) this.wet.shift();
    const drops = this.low ? 1 : big ? 5 : 3;
    for (let i = 0; i < drops; i++) {
      const a = this.rnd() * Math.PI * 2;
      const d = size * (0.55 + this.rnd() * 0.6);
      const s = 0.16 + this.rnd() * 0.14;
      const dx = x + Math.cos(a) * d, dz = z + Math.sin(a) * d;
      this.small.add(dx, this.ground(dx, dz) + 0.032, dz, s, s, 0, this.now, LIFE.splat * 0.5, this.rnd(), 1);
    }
  }

  /** Inked nightmares walking (world x, z by id): a drip every so often behind each. */
  walkers(list: { id: number; x: number; z: number }[]): void {
    const seen = new Set<number>();
    for (const w of list) {
      seen.add(w.id);
      const last = this.dripAt.get(w.id);
      if (last && Math.hypot(w.x - last.x, w.z - last.z) < (this.low ? 1.2 : 0.8)) continue;
      this.dripAt.set(w.id, { x: w.x, z: w.z });
      if (!last) continue;
      const s = 0.17 + this.rnd() * 0.1;
      const ox = (this.rnd() - 0.5) * 0.25, oz = (this.rnd() - 0.5) * 0.25;
      this.small.add(w.x + ox, this.ground(w.x + ox, w.z + oz) + 0.031, w.z + oz, s, s, 0, this.now, LIFE.drip, this.rnd(), 1);
    }
    for (const id of [...this.dripAt.keys()]) if (!seen.has(id)) this.dripAt.delete(id);
  }

  /** Heroes standing (world x, z by id): stepping in fresh ink leaves prints for a while. */
  heroes(list: { id: number; x: number; z: number }[]): void {
    const fresh = LIFE.splat * 0.4;
    for (const h of list) {
      let st = this.inky.get(h.id);
      if (this.wet.some((w) => this.now - w.at < fresh && Math.hypot(h.x - w.x, h.z - w.z) < w.r)) {
        if (!st) {
          st = { until: 0, x: h.x, z: h.z, left: false };
          this.inky.set(h.id, st);
        }
        st.until = this.now + INKY_SECONDS;
      }
      if (!st || this.now > st.until) continue;
      const dx = h.x - st.x, dz = h.z - st.z;
      const d = Math.hypot(dx, dz);
      if (d < STRIDE) continue;
      // Left, right, left: each print a little to the side of the way walked, toes forward.
      const ang = Math.atan2(dx, dz);
      const side = (st.left ? 1 : -1) * 0.11;
      const px = h.x + Math.cos(ang) * side, pz = h.z - Math.sin(ang) * side;
      this.small.add(px, this.ground(px, pz) + 0.033, pz, 0.2, 0.34, ang + Math.PI, this.now, LIFE.foot, this.rnd(), 2);
      st.left = !st.left;
      st.x = h.x;
      st.z = h.z;
    }
  }

  frame(seconds: number): void {
    this.now = seconds;
    this.uniforms.uTime.value = seconds;
  }

  dispose(): void {
    this.splats.dispose();
    this.small.dispose();
  }
}
